import { type ChangeEvent, type KeyboardEvent, type Ref, type RefObject } from 'react'
import { flushSync } from 'react-dom'

import { ButtonDefault, ButtonRoot } from '@/components/common/Button'
import { LoadingMask } from '@/components/common/LoadingMask'
import { Icon } from '@/components/icons/Icon'
import { type IconType } from '@/defs/icon'
import { type TypeaheadOptionType } from '@/defs/input'
import { cn } from '@/utils/commonUtil'

import styles from '../InputTypeaheadContent.module.scss'

export type Props = {
  'aria-label'?: string
  autoFocus?: boolean
  disableClear?: boolean
  disabled?: boolean
  icon: IconType
  isOpen: boolean
  onClear?: () => void
  onKeyDownInput: (e: KeyboardEvent<HTMLInputElement>) => void
  optionCurrent: Partial<TypeaheadOptionType>
  placeholder?: string
  ref?: Ref<HTMLInputElement>
  refInput: RefObject<HTMLInputElement | null>
  required?: boolean
  search: string
  setIsOpen: (isOpen: boolean) => void
  setSearch: (search: string) => void
  showLoadingMask?: boolean
  value?: string
  variant?: 'input' | 'trigger'
}

export const InputTypeaheadContentField = ({
  'aria-label': ariaLabel,
  autoFocus,
  disableClear,
  disabled,
  icon,
  isOpen,
  onClear,
  onKeyDownInput,
  optionCurrent,
  placeholder,
  ref,
  refInput,
  required,
  search,
  setIsOpen,
  setSearch,
  showLoadingMask,
  value,
  variant = 'input',
}: Props) => {
  const isTrigger = variant === 'trigger'

  const { Component, label, labelDisplay, value: valueCurrent } = optionCurrent
  const current = labelDisplay || label || valueCurrent
  const showCurrent = !!current && !isOpen
  const elementCurrent = Component ? (
    <Component>
      <span>{current}</span>
    </Component>
  ) : (
    current
  )

  const onOpen = () => {
    if (disabled) return
    // For iOS, lets the modal input's autoFocus run in the same tap handler which opens the modal
    flushSync(() => setIsOpen(true))
  }

  const onFocus = () => {
    if (disabled) return
    if (value) setSearch('')
    setIsOpen(true)
  }

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    setSearch(e.currentTarget.value)
    setIsOpen(true)
  }

  const onClickClear = () => {
    if (disabled) return
    setSearch('')
    if (!value) {
      refInput.current?.focus()
      return
    }
    setIsOpen(false)
    onClear?.()
  }

  const onClickIcon = () => {
    if (isTrigger) {
      onOpen()
      return
    }
    refInput.current?.focus()
  }

  const showSearchIcon = disableClear || (isTrigger ? !value : !value && !search)

  return (
    <>
      {required && (
        <input
          aria-hidden
          aria-label='Required field'
          className={styles.requiredInput}
          onChange={() => null}
          required
          tabIndex={-1}
          value={value || ''}
        />
      )}
      <div className={styles.field}>
        {!isTrigger && (
          <input
            aria-label={ariaLabel}
            autoComplete='off'
            autoFocus={autoFocus}
            className={cn(styles.input, { showCurrent, styles })}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={onKeyDownInput}
            placeholder={!value ? placeholder : ''}
            ref={ref}
            type='search'
            value={search}
          />
        )}
        {isTrigger && !value && <span className={styles.placeholder}>{placeholder}</span>}
        <div className={cn(styles.current, { showCurrent, styles })}>
          <ButtonDefault as='div' colorScheme='selected' fontNormal size='sm'>
            {elementCurrent}
          </ButtonDefault>
        </div>
        {isTrigger && (
          <ButtonRoot aria-label={placeholder || 'Open Typeahead'} className={styles.button} onClick={onOpen} />
        )}
      </div>
      {showSearchIcon ? (
        <ButtonRoot aria-label='Search' className={styles.icon} onClick={onClickIcon}>
          <Icon name={icon} />
        </ButtonRoot>
      ) : (
        <ButtonRoot aria-label='Clear' className={styles.icon} onClick={onClickClear}>
          <Icon name='xMark' />
        </ButtonRoot>
      )}
      <LoadingMask loading={showLoadingMask} />
    </>
  )
}
